"use client";

import { logError } from "@faire/web--source/common/logging";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { WhenVisibilityIsImminent } from "@faire/web--source/ui/VisibilitySensor/WhenVisibilityIsImminent";
import chunk from "lodash/chunk";
import isUndefined from "lodash/isUndefined";
import React, { CSSProperties, type JSX } from "react";

export type MaybeFake<T> = T & { fake?: boolean };

export interface IProps<T, K> {
  data: ReadonlyArray<T>;
  renderComponent: (value: T, index?: number) => React.JSX.Element;
  batchSize?: number;
  placeholderFn?: (items?: T[], chunkIndex?: number) => React.JSX.Element;
  forceRender?: boolean;
  threshold?: number;
  filterFakeTile?: boolean;
  onBatchVisible?: (
    items: T[],
    chunkIndex: number,
    batchEventData?: K[]
  ) => void;
  /**
   * EfficientListLoader will re-mount when hashKey changes to reset the
   * visibility sensor.
   *
   */
  hashKey: string | undefined;
  useTransition?: boolean;
  renderPlaceholderForNonFirstBatch?: boolean;
  visibilityWrapperDisplayProperty?: CSSProperties["display"];
  eventData?: K[];
  /**
   * A map of group index to component to insert at that group index.
   */
  componentsToInsert?: Map<number, React.ReactNode>;
}

export const EfficientListLoader = <T, MaybeFakeT extends MaybeFake<T>, K>(
  props: IProps<MaybeFakeT | T, K>
) => {
  return <EfficientListLoaderBase key={props.hashKey} {...props} />;
};

export const EfficientListLoaderBase = <T, MaybeFakeT extends MaybeFake<T>, K>(
  props: IProps<MaybeFakeT | T, K>
) => {
  if ((props.batchSize ?? 0) < 0) {
    logError("Batch size must be positive!");
  }

  // if batch size is 0 default to 1
  const batchSize = props.batchSize || 1;

  const batchedData = props.filterFakeTile
    ? chunk(props.data, batchSize).map((chunkData) =>
        chunkData.filter((data) => !(data as MaybeFakeT)?.fake)
      )
    : chunk(props.data, batchSize);

  const batchEventData = props.eventData
    ? chunk(props.eventData, batchSize)
    : undefined;

  return (
    <>
      {batchedData.map((group, groupIndex) => {
        if (groupIndex === 0) {
          return (
            <React.Fragment key={groupIndex}>
              <Group
                key={groupIndex}
                group={group}
                groupIndex={groupIndex}
                renderComponent={props.renderComponent}
                onBatchVisible={props.onBatchVisible}
                batchSize={batchSize}
                visibilityWrapperDisplayProperty={
                  props.visibilityWrapperDisplayProperty
                }
                batchEventData={batchEventData?.[groupIndex]}
              />
              {props.componentsToInsert?.get(groupIndex) ?? null}
            </React.Fragment>
          );
        }

        if (props.renderPlaceholderForNonFirstBatch && props.placeholderFn) {
          return props.placeholderFn(group, groupIndex);
        }

        return (
          <React.Fragment key={groupIndex}>
            <WhenVisibilityIsImminent
              customizePlaceholder={props.placeholderFn?.(group, groupIndex)}
              forceRender={props.forceRender}
              threshold={props.threshold}
              useTransition={props.useTransition}
            >
              <Group
                key={groupIndex}
                group={group}
                groupIndex={groupIndex}
                renderComponent={props.renderComponent}
                onBatchVisible={props.onBatchVisible}
                batchSize={batchSize}
                visibilityWrapperDisplayProperty={
                  props.visibilityWrapperDisplayProperty
                }
                batchEventData={batchEventData?.[groupIndex]}
              />
              {props.componentsToInsert?.get(groupIndex) ?? null}
            </WhenVisibilityIsImminent>
          </React.Fragment>
        );
      })}
    </>
  );
};

type GroupProps<T, MaybeFakeT extends MaybeFake<T>, K> = {
  group: (MaybeFakeT | T)[];
  groupIndex: number;
  renderComponent: (value: T, index?: number) => JSX.Element;
  onBatchVisible?: (
    items: T[],
    chunkIndex: number,
    batchEventData?: K[]
  ) => void;
  batchSize: number;
  visibilityWrapperDisplayProperty?: CSSProperties["display"];
  batchEventData?: K[];
};

const Group = <T, MaybeFakeT extends MaybeFake<T>, K>({
  group,
  groupIndex,
  renderComponent,
  onBatchVisible,
  batchSize,
  visibilityWrapperDisplayProperty = "inherit",
  batchEventData,
}: GroupProps<T, MaybeFakeT, K>) => {
  const viewRef = useDoActionWhenVisible(() => {
    onBatchVisible?.(group, groupIndex, batchEventData);
  });
  if (group.every(isUndefined) && !batchEventData) {
    return (
      <>
        {group.map((item, index) => (
          <React.Fragment key={index}>
            {renderComponent(item, groupIndex * batchSize + index)}
          </React.Fragment>
        ))}
      </>
    );
  } else {
    return (
      <>
        {group.map((item, index) => {
          if ((item as MaybeFakeT)?.fake) {
            return null;
          } else if (index === 0) {
            return (
              <div
                style={{ display: visibilityWrapperDisplayProperty }}
                key={`fireevent-${groupIndex}`}
                ref={viewRef}
              >
                {renderComponent(item, groupIndex * batchSize + index)}
              </div>
            );
          } else {
            return (
              <React.Fragment key={`item-${groupIndex}-${index}`}>
                {renderComponent(item, groupIndex * batchSize + index)}
              </React.Fragment>
            );
          }
        })}
      </>
    );
  }
};
